import React from 'react';
import { Badge } from './ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { HelpCircle } from 'lucide-react';

// Badge component for valuation metrics
export const ValuationBadge = ({ value, badge, metric, showValue = true, showReference = true }) => {
  if (!badge || badge.status === 'unknown') {
    return showValue ? <span className="text-gray-400">N/A</span> : null;
  }

  const getTooltipContent = () => {
    const metricDescriptions = {
      pe: {
        title: "P/E Ratio (Price-to-Earnings)",
        description: "Mesure combien vous payez pour 1€ de bénéfice annuel.",
        interpretation: badge.status === 'attractive' 
          ? "Action potentiellement sous-valorisée" 
          : badge.status === 'elevated' 
            ? "Marché anticipe forte croissance OU surévaluée"
            : "Valorisation dans la norme du secteur"
      },
      pb: {
        title: "P/B Ratio (Price-to-Book)",
        description: "Mesure combien vous payez pour 1€ d'actifs nets.",
        interpretation: badge.status === 'attractive'
          ? "Action trade sous sa valeur comptable - opportunité si ROE >12%"
          : badge.status === 'elevated'
            ? "Premium pour marque/actifs intangibles"
            : "Valorisation dans la norme du secteur"
      },
      roe: {
        title: "ROE (Return on Equity)",
        description: "Rentabilité des capitaux propres.",
        interpretation: badge.status === 'excellent'
          ? "Entreprise très performante"
          : badge.status === 'weak'
            ? "Faible rentabilité - attention"
            : "Rentabilité correcte pour le secteur"
      }
    };

    const info = metricDescriptions[metric] || {};

    return (
      <div className="max-w-xs">
        <div className="font-semibold mb-1">{info.title}</div>
        <div className="text-xs text-gray-400 mb-2">{info.description}</div>
        <div className="text-xs mb-2">
          <span className={`font-medium ${
            badge.color === 'emerald' ? 'text-emerald-400' :
            badge.color === 'yellow' ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {badge.icon} {badge.label}
          </span>
          <span className="text-gray-500 ml-2">({badge.reference})</span>
        </div>
        <div className="text-xs text-gray-400 italic">{info.interpretation}</div>
      </div>
    );
  };

  const formatValue = (val, metric) => {
    if (val === null || val === undefined) return 'N/A';
    if (metric === 'roe') return `${typeof val === 'number' ? val.toFixed(1) : val}%`;
    return typeof val === 'number' ? val.toFixed(2) : val;
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 cursor-help">
            {showValue && (
              <span className="font-semibold text-white">
                {formatValue(value, metric)}{metric !== 'roe' && 'x'}
              </span>
            )}
            <Badge className={`text-xs ${badge.tailwind}`}>
              {badge.icon} {badge.label}
            </Badge>
            {showReference && (
              <span className="text-xs text-gray-500 hidden sm:inline">
                ({badge.reference})
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-gray-900 border-gray-700">
          {getTooltipContent()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Compact badge for table rows
export const ValuationBadgeCompact = ({ badge }) => {
  if (!badge || badge.status === 'unknown') {
    return <span className="text-gray-500">-</span>;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`cursor-help ${
            badge.color === 'emerald' ? 'text-emerald-400' :
            badge.color === 'yellow' ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {badge.icon}
          </span>
        </TooltipTrigger>
        <TooltipContent className="bg-gray-900 border-gray-700">
          <div className="text-sm">
            <span className="font-medium">{badge.label}</span>
            <span className="text-gray-400 ml-2">({badge.reference})</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Valuation Score indicator
export const ValuationScore = ({ score }) => {
  if (score === null || score === undefined) {
    return <span className="text-gray-500">-</span>;
  }

  const getScoreColor = (s) => {
    if (s >= 80) return 'text-emerald-400 bg-emerald-500/20';
    if (s >= 60) return 'text-yellow-400 bg-yellow-500/20';
    return 'text-red-400 bg-red-500/20';
  };

  const getScoreLabel = (s) => {
    if (s >= 80) return '⭐⭐⭐';
    if (s >= 60) return '✅';
    return '⚠️';
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${getScoreColor(score)} cursor-help`}>
            <span>{score}</span>
            <span className="text-xs">/100</span>
            <span className="ml-1">{getScoreLabel(score)}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-gray-900 border-gray-700">
          <div className="max-w-xs">
            <div className="font-semibold mb-1">Score de Valorisation</div>
            <div className="text-xs text-gray-400">
              Basé sur P/E, P/B et ROE comparés aux fourchettes sectorielles.
            </div>
            <div className="mt-2 text-xs">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-emerald-400">80-100</span>
                <span className="text-gray-400">Action sous-valorisée ⭐⭐⭐</span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-yellow-400">60-79</span>
                <span className="text-gray-400">Valorisation équilibrée ✅</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-red-400">0-59</span>
                <span className="text-gray-400">Valorisation élevée ⚠️</span>
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ValuationBadge;
